import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  VStack,
  HStack,
  Spinner,
  FormControl,
  FormLabel,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEditableRoles,
  getRolePrivileges,
  saveRolePrivileges,
  getRoleFieldPrivileges,
  saveRoleFieldPrivileges,
} from '../actions/privilegeActions';
import { fetchCompanies } from '../actions/companyActions';
import { usePrivilege } from '../hooks/usePrivilege';
import { useAppToast } from '../hooks/useAppToast';
import { FIELD_CATALOG, allFieldsTrueMap } from '../utils/fieldCatalog';

const PrivilegeManagement = () => {
  const appToast = useAppToast();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companies = useSelector((state) => state.company?.companies || state.company?.list || []);
  const { can } = usePrivilege();
  const isSuperAdmin = user?.role === 'super_admin';

  const [tabIndex, setTabIndex] = useState(0);
  const [editableRoles, setEditableRoles] = useState([]);
  const [targetRole, setTargetRole] = useState('');
  const [companyId, setCompanyId] = useState('');

  // Basic Rules
  const [privileges, setPrivileges] = useState({});
  const [callerPrivileges, setCallerPrivileges] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [basicDirty, setBasicDirty] = useState(false);
  const [basicLoading, setBasicLoading] = useState(false);
  const [basicSaving, setBasicSaving] = useState(false);

  // Field Visibility
  const [fieldCatalog, setFieldCatalog] = useState(FIELD_CATALOG);
  const [fields, setFields] = useState(() => allFieldsTrueMap());
  const [fieldDirty, setFieldDirty] = useState(false);
  const [fieldLoading, setFieldLoading] = useState(false);
  const [fieldSaving, setFieldSaving] = useState(false);

  const [rolesLoading, setRolesLoading] = useState(true);

  const dirty = tabIndex === 0 ? basicDirty : fieldDirty;
  const saving = tabIndex === 0 ? basicSaving : fieldSaving;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const roles = await getEditableRoles();
        if (!cancelled) {
          setEditableRoles(roles);
          if (roles.length && !targetRole) {
            setTargetRole(roles[0].role);
          }
        }
      } catch (err) {
        appToast.showApiError(err, 'Failed to load roles');
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, isSuperAdmin]);

  const companyOptions = Array.isArray(companies)
    ? companies
    : companies?.data || [];

  const loadBasic = useCallback(async () => {
    if (!targetRole) return;
    if (isSuperAdmin && !companyId) {
      setPrivileges({});
      setCatalog([]);
      return;
    }
    setBasicLoading(true);
    try {
      const data = await getRolePrivileges({
        targetRole,
        company_id: isSuperAdmin ? companyId : undefined,
      });
      setPrivileges(data.privileges || {});
      setCatalog(Array.isArray(data.catalog) ? data.catalog : []);
      setCallerPrivileges(
        isSuperAdmin
          ? Object.fromEntries(
              Object.keys(data.privileges || {}).map((k) => [k, true])
            )
          : data.callerPrivileges || {}
      );
      setBasicDirty(false);
    } catch (err) {
      appToast.showApiError(err, 'Failed to load privileges');
    } finally {
      setBasicLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRole, companyId, isSuperAdmin]);

  const loadFields = useCallback(async () => {
    if (!targetRole) return;
    if (isSuperAdmin && !companyId) {
      setFields(allFieldsTrueMap());
      return;
    }
    setFieldLoading(true);
    try {
      const data = await getRoleFieldPrivileges({
        targetRole,
        company_id: isSuperAdmin ? companyId : undefined,
      });
      if (Array.isArray(data.catalog) && data.catalog.length) {
        setFieldCatalog(data.catalog);
      }
      setFields(data.fields || allFieldsTrueMap());
      setFieldDirty(false);
    } catch (err) {
      appToast.showApiError(err, 'Failed to load field privileges');
    } finally {
      setFieldLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRole, companyId, isSuperAdmin]);

  useEffect(() => {
    loadBasic();
  }, [loadBasic]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const confirmDiscard = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) {
      return false;
    }
    return true;
  };

  const handleRoleChange = (nextRole) => {
    if (!confirmDiscard()) return;
    setTargetRole(nextRole);
  };

  const handleCompanyChange = (nextId) => {
    if (!confirmDiscard()) return;
    setCompanyId(nextId);
  };

  const handleTabChange = (index) => {
    if (index !== tabIndex && !confirmDiscard()) return;
    setTabIndex(index);
  };

  const canGrant = (key) => {
    if (isSuperAdmin) return true;
    return Boolean(callerPrivileges[key]);
  };

  const handleBasicToggle = (key, checked) => {
    if (!canGrant(key) && checked) return;
    setPrivileges((prev) => ({ ...prev, [key]: checked }));
    setBasicDirty(true);
  };

  const handleFieldToggle = (entity, fieldKey, view, checked) => {
    setFields((prev) => ({
      ...prev,
      [entity]: {
        ...(prev[entity] || {}),
        [fieldKey]: {
          list: prev[entity]?.[fieldKey]?.list !== false,
          form: prev[entity]?.[fieldKey]?.form !== false,
          [view]: checked,
        },
      },
    }));
    setFieldDirty(true);
  };

  const handleSave = async () => {
    if (!targetRole) return;
    if (isSuperAdmin && !companyId) {
      appToast.showWarning('Select a company');
      return;
    }
    if (tabIndex === 0) {
      setBasicSaving(true);
      try {
        await saveRolePrivileges({
          targetRole,
          privileges,
          company_id: isSuperAdmin ? companyId : undefined,
        });
        setBasicDirty(false);
        appToast.showSuccess('Basic rules saved');
      } catch (err) {
        appToast.showApiError(err, 'Save failed');
      } finally {
        setBasicSaving(false);
      }
    } else {
      setFieldSaving(true);
      try {
        await saveRoleFieldPrivileges({
          targetRole,
          fields,
          company_id: isSuperAdmin ? companyId : undefined,
        });
        setFieldDirty(false);
        appToast.showSuccess('Field visibility saved');
      } catch (err) {
        appToast.showApiError(err, 'Save failed');
      } finally {
        setFieldSaving(false);
      }
    }
  };

  if (!can('privilege.manage')) {
    return (
      <Box p={4}>
        <Text>You do not have access to Privilege Management.</Text>
      </Box>
    );
  }

  const needsCompany = isSuperAdmin && !companyId;
  const roleLabel = editableRoles.find((r) => r.role === targetRole)?.label || targetRole;

  return (
    <Box maxW="1100px" mx="auto">
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="lg">User Privileges</Heading>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!dirty || !targetRole || needsCompany}
        >
          Save Changes
        </Button>
      </Flex>

      <HStack spacing={4} mb={4} align="flex-end" flexWrap="wrap">
        {isSuperAdmin && (
          <FormControl maxW="280px">
            <FormLabel mb={1}>Company</FormLabel>
            <Select
              placeholder="Select company"
              value={companyId}
              onChange={(e) => handleCompanyChange(e.target.value)}
            >
              {companyOptions.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name || c.slug}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl maxW="280px">
          <FormLabel mb={1}>Role</FormLabel>
          {rolesLoading ? (
            <Spinner size="sm" />
          ) : (
            <Select
              value={targetRole}
              onChange={(e) => handleRoleChange(e.target.value)}
              placeholder="Select role"
            >
              {editableRoles.map((r) => (
                <option key={r.role} value={r.role}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
      </HStack>

      {targetRole && (
        <Heading size="md" mb={4} pb={2} borderBottom="2px solid" borderColor="blue.400">
          {roleLabel}
        </Heading>
      )}

      <Tabs index={tabIndex} onChange={handleTabChange} colorScheme="blue" isLazy>
        <TabList>
          <Tab>Basic Rules</Tab>
          <Tab>Field Visibility</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            {basicLoading ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : needsCompany ? (
              <Text color="gray.600">Select a company to manage role privileges.</Text>
            ) : !targetRole ? (
              <Text color="gray.600">No subordinate roles available to edit.</Text>
            ) : catalog.length === 0 ? (
              <Text color="gray.600">
                No privilege options are enabled for this role in Privilege Catalog.
              </Text>
            ) : (
              <VStack spacing={4} align="stretch">
                {catalog.map((group) => (
                  <Box
                    key={group.group}
                    bg={group.color}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                    p={4}
                  >
                    <Text
                      fontWeight="bold"
                      mb={3}
                      fontSize="sm"
                      color="gray.600"
                      textTransform="uppercase"
                    >
                      {group.group}
                    </Text>
                    <Divider mb={3} />
                    <VStack spacing={2} align="stretch">
                      {group.items.map((item) => {
                        const grantable = canGrant(item.key);
                        const checked = Boolean(privileges[item.key]);
                        return (
                          <Flex
                            key={item.key}
                            justify="space-between"
                            align="center"
                            py={1}
                            opacity={grantable ? 1 : 0.55}
                          >
                            <Text fontSize="md">{item.label}</Text>
                            <HStack spacing={2}>
                              <Text fontSize="xs" color="gray.500">
                                Off
                              </Text>
                              <Switch
                                colorScheme="blue"
                                isChecked={checked}
                                isDisabled={!grantable}
                                onChange={(e) => handleBasicToggle(item.key, e.target.checked)}
                              />
                              <Text fontSize="xs" color="gray.500">
                                ON
                              </Text>
                            </HStack>
                          </Flex>
                        );
                      })}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            )}
          </TabPanel>

          <TabPanel px={0}>
            {fieldLoading ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : needsCompany ? (
              <Text color="gray.600">Select a company to manage field visibility.</Text>
            ) : !targetRole ? (
              <Text color="gray.600">No subordinate roles available to edit.</Text>
            ) : (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Uncheck List view to hide a column in tables. Uncheck Form view to hide a field in
                  create/edit forms. Report tables support List view only.
                </Text>
                {fieldCatalog.map((entity) => {
                  const listOnly = Boolean(entity.listOnly);
                  return (
                  <Box
                    key={entity.entity}
                    bg={entity.color}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                    p={4}
                    overflowX="auto"
                  >
                    <Text
                      fontWeight="bold"
                      mb={3}
                      fontSize="sm"
                      color="gray.600"
                      textTransform="uppercase"
                    >
                      {entity.label}
                      {listOnly ? ' (List only)' : ''}
                    </Text>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Column / Field</Th>
                          <Th textAlign="center" w="120px">
                            List view
                          </Th>
                          {!listOnly && (
                          <Th textAlign="center" w="120px">
                            Form view
                          </Th>
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {entity.fields.map((field) => {
                          const cell = fields?.[entity.entity]?.[field.key] || {
                            list: true,
                            form: true,
                          };
                          const hasList = (field.listAccessors || []).length > 0;
                          const hasForm = !listOnly && (field.formAccessors || []).length > 0;
                          return (
                            <Tr key={field.key}>
                              <Td>{field.label}</Td>
                              <Td textAlign="center">
                                {hasList ? (
                                  <Checkbox
                                    isChecked={cell.list !== false}
                                    onChange={(e) =>
                                      handleFieldToggle(
                                        entity.entity,
                                        field.key,
                                        'list',
                                        e.target.checked
                                      )
                                    }
                                  />
                                ) : (
                                  <Text fontSize="xs" color="gray.400">
                                    —
                                  </Text>
                                )}
                              </Td>
                              {!listOnly && (
                              <Td textAlign="center">
                                {hasForm ? (
                                  <Checkbox
                                    isChecked={cell.form !== false}
                                    onChange={(e) =>
                                      handleFieldToggle(
                                        entity.entity,
                                        field.key,
                                        'form',
                                        e.target.checked
                                      )
                                    }
                                  />
                                ) : (
                                  <Text fontSize="xs" color="gray.400">
                                    —
                                  </Text>
                                )}
                              </Td>
                              )}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                  );
                })}
              </VStack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default PrivilegeManagement;
